import { useCallback, useEffect, useRef } from "react";
import { useRouterContext } from "../../contexts";
import { signIn as authSignIn, useSession } from "next-auth/react";
import { useUser } from "../../contexts";
import type { UserProfile } from "../../contexts";
import { LS_Set, LS_Get } from "../../utils/local-storage";
import { createNotification, googleEvent, changeHash } from "../../utils/dom";
import { LoginFormFields } from "./useLoginForm";
import shared from "../../data/js/shared";
import {
	trackFunnelEvent,
	trackError,
	trackClick,
	trackApiRequest,
	trackApiResponse,
	trackComplete,
	getFunnelSessionId,
} from "../../utils/track-funnel";
import { ErrorSeverity, trackError as trackProductionError } from "../../utils/error-tracking";
import { severityForOwnFetchFailure, transportFailureContext } from "../../utils/fetch-transport-failure";
import { parseJsonResponse } from "../../utils/read-json-response";
import { CODE_EXPIRED_ERROR, oauthVerifyPathFromError } from "../../utils/auth-signin-errors";
import { hasFirstName, splitFullName } from "../../utils/person-name";
import { LOGIN_CODE_SENT_TO_KEY } from "../../utils/login-code";
import { pendingGuestSellerName } from "../../utils/guest-sell-draft";
import { saveProfileName } from "../../utils/save-profile-name";
import { nanpPhoneProblem, PHONE_FORMAT_MESSAGE } from "../../utils/phone-messages";
import { UNDELIVERABLE_SMS_MESSAGES, type CodeDelivery } from "../../utils/sms-delivery-messages";
import { postWithBotChallenge } from "../../utils/bot-challenge-client";
import {
	appleAuthorizeUrl,
	isAppleDeepLink,
	joinNameParts,
	readAppleDeepLink,
	type AppleDeepLinkToken,
} from "../../utils/apple-deep-link";

/**
 * Where the last login code went -- the number or address, as sent.
 *
 * The code step checks the code against the phone box, and the box stays in the
 * form (only hidden) and stays editable: back, edit, forward, and the code in
 * hand belongs to a number the box no longer holds. On 2026-09-22 a flow sent a
 * code to one number and checked one against another ten seconds later. Kept in
 * storage so a reload on the code step still knows where the code went.
 */
const CODE_SENT_TO_KEY = LOGIN_CODE_SENT_TO_KEY;

/**
 * Phone sign-up (utils/phone-signup.ts): a number no account uses is proven,
 * not signed in. PHONE_SIGNUP_KEY holds the number send-code called new;
 * PHONE_PROOF_KEY holds the proof until an email sign-in attaches the number.
 */
const PHONE_SIGNUP_KEY = 'phoneSignupPending';
const PHONE_PROOF_KEY = 'phoneSignupProof';

/** What verifyLoginOTP and its phone sign-up step answer. */
export interface LoginStepResult {
	success: boolean;
	error?: string;
	profile?: UserProfile;
}

/** verifyLoginOTP's answer once a new number is proven: ask for an email next. */
export const PHONE_NEEDS_EMAIL = 'phone_needs_email';

/**
 * What the login endpoints answer with. They are parsed through
 * `parseJsonResponse` so a platform error page (a Vercel 504 timeout, an edge
 * refusal) is reported with its endpoint and status instead of as a bare
 * `Unexpected token 'A'` SyntaxError that names neither.
 */
interface LoginApiBody {
	success?: boolean;
	error?: string;
	errorCode?: string;
	profile?: UserProfile;
	/** send-code: no open account uses this number. */
	phoneSignup?: boolean;
	/** /api/auth/phone-signup/start: the proof of the number. */
	proofId?: string;
	proofToken?: string;
	/** verify-email's EMAIL_HAS_ACCOUNT: a login code for the email's own account. */
	signInCode?: string;
}

/** What /api/auth/code-delivery answers. Read loosely, like the rest: an open tab can be older than the route. */
interface CodeDeliveryBody {
	success?: boolean;
	delivery?: CodeDelivery;
	message?: string;
}

/**
 * File a login request that failed before it was answered, naming the request
 * and the browser state at the catch.
 *
 * On 2026-10-15 one iPhone session filed a bare Safari `Load failed` from
 * checkCodeDelivery and another from sendEmailVerification five minutes later.
 * The second action makes up to four requests and the row named none of them,
 * nor whether the tab was offline or leaving. `request` and the transport
 * fields answer both; a cut the browser caused (offline, unloading) files at
 * info, the same rule the product page uses.
 */
function reportLoginFetchFailure(error: unknown, action: string, request: string): void {
	const transport = transportFailureContext(error);
	trackProductionError(
		error instanceof Error ? error : String(error),
		severityForOwnFetchFailure(error, ErrorSeverity.ERROR, transport),
		{ action, component: 'useLoginAuth', extra: { request, ...transport } },
	);
}

/** File a login fault that is not a transport failure, as an Error or its text. */
function reportLoginError(error: unknown, action: string): void {
	trackProductionError(error instanceof Error ? error : String(error), undefined, { action, component: 'useLoginAuth' });
}

/** The message a funnel error row carries for a throw: its own, or `fallback` for a non-Error. */
function thrownMessage(error: unknown, fallback: string): string {
	return error instanceof Error ? error.message : fallback;
}

/** A plugin rejection as an Error: Capacitor plugins can reject with a bare value. */
function thrownAsError(error: unknown, fallback: string): Error {
	return error instanceof Error ? error : new Error(fallback);
}

type SocialProvider = 'google' | 'facebook' | 'apple';

/** What a native SDK hands back, forwarded to NextAuth's native-oauth provider. */
interface NativeSignInToken {
	providerToken: string;
	email?: string | null;
	name?: string | null;
	userId?: string | null;
}

const {
	oldTokenMsg,
	emailTakenMsg,
} = shared.login;

export interface UseLoginAuthProps {
	fields: LoginFormFields;
	otpCode: string;
	executeRecaptcha: (action?: string) => Promise<string | undefined>;
	onInteraction?: () => void;
}

/**
 * useLoginAuth Hook
 *
 * Handles all authentication operations including:
 * - Passwordless OTP login
 * - Social login (Google, Facebook, Apple)
 * - OTP verification
 * - Profile completion
 *
 * @param fields - Login form fields
 * @param otpCode - Current OTP code
 * @param executeRecaptcha - Function to execute reCAPTCHA
 * @returns Object containing authentication methods
 */
export const useLoginAuth = ({
	fields,
	otpCode,
	executeRecaptcha,
	onInteraction,
}: UseLoginAuthProps) => {
	const { asPath, push } = useRouterContext();
	/** Where a finished login lands: the page it started on. */
	const returnPath = asPath || '/';
	const { fetchUserProfile, setProfile, autoLogin, setLoggedIn } = useUser();
	const { update: updateSession } = useSession();
	/**
	 * The `update` from the latest render, for work that runs after a sign-in
	 * inside a callback created before it.
	 *
	 * next-auth 4's `update()` returns without doing anything when the session
	 * it was rendered with is empty (`if (loading || !session) return`). The
	 * code step's callback is created while the visitor is signed out, so the
	 * `updateSession` it closes over can never refresh the session it creates:
	 * the guest-sell name reached the row and the JWT kept `firstName: null`,
	 * which only the 2026-10-02 e2e run caught.
	 */
	const latestUpdateSessionRef = useRef(updateSession);
	useEffect(() => {
		latestUpdateSessionRef.current = updateSession;
	}, [updateSession]);

	/**
	 * Clean up after successful login
	 */
	const cleanupLoginSuccess = useCallback(async () => {
		await autoLogin();
		setLoggedIn(true);
		setTimeout(() => {
			// Not while the more-info step is up. useLoginFlow calls this the
			// moment a session exists, which for a new account is the moment its
			// code is accepted -- the same moment it is sent to more-info. Clearing
			// the hash then closed the step two seconds after it opened, before
			// anyone could type a name. Submitting the step calls this again from
			// `welcome`, which is cleared as before.
			if (window.location.hash === "#more-info") return;
			changeHash("");
		}, 2000);
	}, [autoLogin, setLoggedIn]);

	/**
	 * Fetch profile from NextAuth session
	 */
	const fetchProfileFromSession = useCallback(async () => {
		try {
			const response = await fetch('/api/auth/session');
			const session = await response.json();

			if (session?.profile) {
				return session.profile;
			} else if (session?.user) {
				return {
					id: session.user.id,
					email: session.user.email,
					phone: null,
					firstName: null,
					lastName: null,
					username: session.user.name,
				};
			}
			return null;
		} catch (error) {
			reportLoginError(error, 'fetchProfileFromSession');
			return null;
		}
	}, []);

	/**
	 * Send OTP code for login
	 */
	const sendLoginCode = useCallback(async () => {
		let { phone } = fields;
		const { countryCode } = fields;
		let email: string | undefined;

		const ssPhone = LS_Get<string>("phone");
		if (!phone && ssPhone) {
			phone = ssPhone;
		}

		if (phone.includes("@")) {
			email = phone;
			phone = "";
			LS_Set("phone", email);
		} else {
			// Strip non-digit characters (keep + for international format)
			phone = phone.replace(/[^+\d]/g, "");

			// Auto-prepend country code if phone doesn't already have one
			if (phone && !phone.startsWith("+")) {
				const dialCode = countryCode?.phone || 1; // Default to US (+1)
				// If US and phone starts with 1 + 10 digits, user already included country code
				if (dialCode === 1 && phone.startsWith("1") && phone.length === 11) {
					phone = `+${phone}`;
				} else {
					phone = `+${dialCode}${phone}`;
				}
			}

			// US/Canada (+1): 10 national digits, and neither the area code nor
			// the exchange may start with 0 or 1
			const phoneProblem = nanpPhoneProblem(phone);
			if (phoneProblem) {
				createNotification({ type: "error", msg: phoneProblem });
				return { success: false, error: 'invalid_phone' };
			}

			LS_Set("phone", phone);
			LS_Set("countryCode", countryCode);
		}

		const recaptchaToken = await executeRecaptcha('login');

		try {
			// A low score comes back as a checkbox to tick; postWithBotChallenge
			// shows it and resends.
			const response = await postWithBotChallenge('/api/auth/send-code', {
				emailOrPhone: phone || email,
				recaptchaToken,
				// The OTP events are emitted server-side, so the server needs this
				// browser's funnel session to file them under -- otherwise each one
				// becomes its own single-event session and the funnel drill-down has
				// no flow to show.
				funnelSessionId: getFunnelSessionId('login'),
			});

			const data = await parseJsonResponse<LoginApiBody>(response, "POST /api/auth/send-code");

			if (data.success) {
				// Note: otp_sent tracked server-side in /api/auth/send-code
				LS_Set(CODE_SENT_TO_KEY, phone || email);
				LS_Set(PHONE_SIGNUP_KEY, phone && data.phoneSignup ? phone : null);
				changeHash("login-otp");
				return { success: true };
			} else {
				// Track OTP send error. A request reCAPTCHA calls a bot comes back
				// UNAUTHORIZED (401) below.
				trackError('login', 'code_send', data.error || 'Failed to send OTP code', {
					email: email || undefined,
					phone: phone || undefined,
					severity: 'error',
				});
				// Show user-friendly error message
				// A country we are not permitted to text is NOT a formatting
				// mistake, so it has to come off the phone-error path before that
				// path's keyword sniff claims it. The number was right -- telling a
				// +92 visitor to add the country code they already typed is how they
				// get stuck retyping a valid number.
				const isRegionError = data.errorCode === 'PHONE_REGION_UNSUPPORTED';
				// No new accounts by phone. Checked before the keyword sniff below,
				// which would read "phone" in the message as a mistyped number.
				const isSignupClosed = data.errorCode === 'PHONE_SIGNUP_CLOSED';
				const isPhoneError = !isRegionError && !isSignupClosed && (data.errorCode === 'INVALID_PHONE' || data.error?.includes('Twilio') || data.error?.includes('Phone'));
				const userMessage = isRegionError || isSignupClosed
					? data.error
					// The server words INVALID_PHONE by the number's shape (a typo vs a
					// missing country code), so its message is the specific one.
					: isPhoneError
					? (data.errorCode === 'INVALID_PHONE' && data.error) || PHONE_FORMAT_MESSAGE
					: data.errorCode === 'INVALID_FORMAT'
					? "Please enter a valid email or phone number."
					: data.errorCode === 'EMAIL_BANNED' || data.errorCode === 'ACCOUNT_BANNED'
					? data.error
					// Mandrill will never deliver to this address, so "please try again"
					// is advice that cannot work no matter how many times it is taken.
					// The server writes the only thing that can -- use another address,
					// or your phone -- so pass it straight through.
					: data.errorCode === 'EMAIL_UNDELIVERABLE'
					? data.error
					// `…@agmail.com`: the server names the address it thinks was meant.
					: data.errorCode === 'EMAIL_DOMAIN_TYPO'
					? data.error
					// A text the server will not send (a cap was hit). Its message
					// points at email, which is the way in; "try again" is not.
					: data.errorCode === 'SMS_UNAVAILABLE'
					? data.error
					// reCAPTCHA called this browser a bot, or the page never got a token.
					// The server's message says to reload, which is what fixes the second.
					: data.errorCode === 'UNAUTHORIZED'
					? data.error
					// A code went out less than a minute ago and still works. The
					// server says so and how long to wait; "please try again" sent
					// people straight back into the same refusal.
					: data.errorCode === 'CODE_COOLDOWN'
					? data.error
					: "Unable to send verification code. Please try again.";
				createNotification({
					type: "error",
					msg: userMessage,
				});
				// The cooldown is keyed on this email or phone, so the code it
				// refers to went HERE and still works. The message says "use that
				// one", so open the box to type it in -- left on the entry view,
				// the only way forward was to wait out the cooldown and resend.
				if (data.errorCode === 'CODE_COOLDOWN') {
					LS_Set(CODE_SENT_TO_KEY, phone || email);
					changeHash("login-otp");
				}
				// No code will ever reach this address, so the way forward is to fix
				// it -- usually a typo before the @, which no domain check can catch.
				// A resend from the code step used to leave the person on that step,
				// where "Send a new code" could only hit the same dead address again:
				// on 2026-10-23 one visitor resent twice to a mistyped @yahoo.com
				// before finding their way back. The entry view keeps what they typed.
				if (data.errorCode === 'EMAIL_UNDELIVERABLE' || data.errorCode === 'EMAIL_DOMAIN_TYPO') {
					changeHash("");
				}
				return { success: false, errorCode: data.errorCode };
			}
		} catch (error) {
			reportLoginError(error, 'sendLoginCode');

			// Track network error
			trackError('login', 'network', thrownMessage(error, 'Network error sending OTP code'), {
				email: email || undefined,
				phone: phone || undefined,
				severity: 'error',
			});

			createNotification({
				type: "error",
				msg: "Error sending verification code. Try again please.",
			});
			return { success: false, error: 'network_error' };
		}
	}, [fields, executeRecaptcha]);

	/**
	 * Ask whether the login text arrived. Null when the code went to an email,
	 * no send is on record in this browser, or there was no answer to read --
	 * the code step works the same either way.
	 */
	const checkCodeDelivery = useCallback(async (): Promise<{ delivery: CodeDelivery; message?: string } | null> => {
		const sentTo = LS_Get<string>(CODE_SENT_TO_KEY);
		if (!sentTo || sentTo.includes("@")) return null;
		try {
			const response = await fetch('/api/auth/code-delivery', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ phone: sentTo, funnelSessionId: getFunnelSessionId('login') }),
			});
			const data = await parseJsonResponse<CodeDeliveryBody>(response, "POST /api/auth/code-delivery");
			return data.success && data.delivery ? { delivery: data.delivery, message: data.message } : null;
		} catch (error) {
			reportLoginFetchFailure(error, 'checkCodeDelivery', 'POST /api/auth/code-delivery');
			return null;
		}
	}, []);

	/**
	 * Redeem a new number's code for a proof, then send the person to the email
	 * step: no account is made from a phone (utils/phone-signup.ts).
	 */
	const startPhoneSignup = useCallback(async (phone: string): Promise<LoginStepResult> => {
		try {
			const response = await fetch('/api/auth/phone-signup/start', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ phone, code: otpCode }),
			});
			const data = await parseJsonResponse<LoginApiBody>(response, "POST /api/auth/phone-signup/start");
			if (!data.success || !data.proofId || !data.proofToken) {
				return { success: false, error: data.error || 'Invalid verification code' };
			}
			LS_Set(PHONE_PROOF_KEY, { phone, proofId: data.proofId, proofToken: data.proofToken });
			LS_Set(PHONE_SIGNUP_KEY, null);
			LS_Set(CODE_SENT_TO_KEY, null);
			createNotification({ type: "info", msg: shared.login.phoneSignupNeedsEmail });
			return { success: false, error: PHONE_NEEDS_EMAIL };
		} catch (error) {
			reportLoginError(error, 'startPhoneSignup');
			return { success: false, error: 'network_error' };
		}
	}, [otpCode]);

	/**
	 * After an email sign-in, put a number proven by startPhoneSignup on the
	 * account. A failure only means the number is not added; sign-in stands.
	 */
	const attachProvenPhone = useCallback(async (profile: UserProfile) => {
		const proof = LS_Get<{ phone: string; proofId: string; proofToken: string }>(PHONE_PROOF_KEY);
		if (!proof?.proofId || !proof.proofToken) return;
		LS_Set(PHONE_PROOF_KEY, null);
		try {
			const response = await fetch('/api/auth/phone-signup/attach', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ proofId: proof.proofId, proofToken: proof.proofToken }),
			});
			const data = await parseJsonResponse<LoginApiBody & { attached?: boolean }>(response, "POST /api/auth/phone-signup/attach");
			if (data.success && data.attached) profile.phone = proof.phone;
		} catch (error) {
			reportLoginError(error, 'attachProvenPhone');
		}
	}, []);

	/**
	 * Verify OTP and login
	 */
	const verifyLoginOTP = useCallback(async (): Promise<LoginStepResult> => {
		let { phone, countryCode } = fields;
		let email: string | undefined;

		const ssPhone = LS_Get<string>("phone");
		const ssCC = LS_Get<LoginFormFields["countryCode"]>("countryCode");

		if (!phone && ssPhone) {
			phone = ssPhone;
			countryCode = ssCC as LoginFormFields["countryCode"];
		}

		if (phone.includes("@")) {
			email = phone;
			phone = "";
		} else {
			// Strip non-digit characters (keep + for international format)
			phone = phone.replace(/[^+\d]/g, "");

			// Auto-prepend country code if phone doesn't already have one
			if (phone && !phone.startsWith("+")) {
				const dialCode = countryCode?.phone || 1; // Default to US (+1)
				// If US and phone starts with 1 + 10 digits, user already included country code
				if (dialCode === 1 && phone.startsWith("1") && phone.length === 11) {
					phone = `+${phone}`;
				} else {
					phone = `+${dialCode}${phone}`;
				}
			}

			// US/Canada (+1): 10 national digits, and neither the area code nor
			// the exchange may start with 0 or 1
			const phoneProblem = nanpPhoneProblem(phone);
			if (phoneProblem) {
				createNotification({ type: "error", msg: phoneProblem });
				return { success: false, error: 'invalid_phone' };
			}
		}

		if (!phone && !email) {
			changeHash("");
			return { success: false };
		}

		if (!otpCode || otpCode.length !== 6) {
			return { success: false, error: 'invalid_otp' };
		}

		// The code belongs to where it was sent, not to whatever the box holds
		// now. Only checked when this browser recorded a send: with storage
		// blocked there is no record, and refusing then would strand the person
		// holding a good code.
		const sentTo = LS_Get<string>(CODE_SENT_TO_KEY);
		const target = email || phone;
		if (sentTo && sentTo !== target) {
			createNotification({
				type: "error",
				msg: `Your code went to ${sentTo}. To use ${target}, send a new code.`,
			});
			changeHash("");
			return { success: false, error: 'code_sent_elsewhere' };
		}

		// Track OTP code entered (client-side for UX timing)
		trackFunnelEvent('login', 'otp_entered', {
			email: email || undefined,
			phone: phone || undefined,
		});

		// A new number: its code proves the number, and the person signs in
		// with an email next. Sign-in never makes an account from a phone.
		if (phone && !email && LS_Get<string>(PHONE_SIGNUP_KEY) === phone) {
			return startPhoneSignup(phone);
		}

		try {
			const result = await authSignIn('passwordless', {
				emailOrPhone: email || phone,
				code: otpCode,
				// Carried so the otp_verified / otp_failed events the provider emits
				// join this browser's login funnel session instead of each becoming a
				// session of one.
				funnelSessionId: getFunnelSessionId('login'),
				redirect: false,
			});

			if (result?.error) {
				// Note: otp_failed tracked server-side by the passwordless provider.
				// A wrong code and a spent guess budget are both said under the code
				// boxes by the flow (useLoginFlow), which keeps the code step on
				// screen. A wrong code used to swap the form for a separate
				// "not the correct digits" screen whose Try Again started over.
				if (
					result.error.includes('Invalid verification code') ||
					result.error === CODE_EXPIRED_ERROR ||
					result.error.startsWith('Too many wrong codes') ||
					// The text never arrived (a landline, no such number): said inline too.
					UNDELIVERABLE_SMS_MESSAGES.has(result.error)
				) {
					// Handled inline by the caller.
				} else {
					createNotification({
						type: "error",
						msg: "Error verifying code. Try again please.",
					});
				}
				return { success: false, error: result.error };
			}

			if (result?.ok) {
				LS_Set(CODE_SENT_TO_KEY, null);
				const profile = await fetchProfileFromSession();

				if (!profile) {
					createNotification({
						type: "error",
						msg: "Error fetching profile. Try again please.",
					});

					// Track profile fetch error
					trackError('login', 'profile_fetch', 'Failed to fetch profile after OTP verification', {
						email: email || undefined,
						phone: phone || undefined,
						severity: 'error',
					});

					return { success: false, error: 'profile_fetch_error' };
				}

				// Note: otp_verified tracked server-side by the passwordless provider

				// Signed in by email after proving a new number: the number goes on
				// this account now.
				if (email) await attachProvenPhone(profile);

				// A guest seller typed their name before the account existed. Put it
				// on the account now rather than asking for it again in more-info.
				const pendingName = hasFirstName(profile) ? null : pendingGuestSellerName(email);
				if (pendingName) {
					const saved = await saveProfileName(pendingName);
					if (saved.success && saved.firstName) {
						await latestUpdateSessionRef.current();
						const { lastName } = splitFullName(pendingName);
						profile.firstName = saved.firstName;
						profile.lastName = lastName || profile.lastName;
					}
				}

				setProfile(profile);
				googleEvent("new_user", profile);

				const isNewUser = !hasFirstName(profile) || !profile.email;
				if (isNewUser) {
					// New user needs to complete profile - complete tracked in updateUserProfile
					changeHash("more-info");
				} else {
					// Existing user with complete profile - track login complete
					const method = email ? 'email_otp' : 'phone_otp';
					trackComplete('login', {
						user_id: profile.id,
						email: profile.email,
						phone: phone || undefined,
						method,
						is_new_user: false,
						redirect_url: returnPath,
					});
					changeHash("loggedin");
					await cleanupLoginSuccess();
				}
				return { success: true, profile };
			}
		} catch (error) {
			reportLoginError(error, 'verifyLoginOTP');

			// Track network error
			trackError('login', 'network', thrownMessage(error, 'Network error during OTP verification'), {
				email: email || undefined,
				phone: phone || undefined,
				severity: 'error',
			});

			createNotification({
				type: "error",
				msg: "Error sending to server. Try again please.",
			});
			return { success: false, error: 'network_error' };
		}
	}, [fields, otpCode, fetchProfileFromSession, setProfile, cleanupLoginSuccess, returnPath, startPhoneSignup, attachProvenPhone]);

	/**
	 * Finish the more-info step with a name and no email.
	 *
	 * The session is refreshed before the profile is re-read: the profile is
	 * seeded from the JWT, so without `updateSession()` every later page would
	 * still see `firstName: null` and ask again.
	 */
	const saveNameOnly = useCallback(async (firstName: string, lastName?: string) => {
		try {
			const response = await fetch('/api/user/profile', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ firstName, lastName }),
			});
			const data = await parseJsonResponse<LoginApiBody>(response, "POST /api/user/profile");

			if (!response.ok || !data.success) {
				trackError('login', 'profile_update', data.error || 'Failed to save name', {
					context: 'more_info_name_only',
				});
				return { success: false, error: data.error || 'We could not save your name. Try again please.' };
			}

			await updateSession();
			const profile = await fetchUserProfile(true);
			if (profile) setProfile(profile);

			trackComplete('login', {
				user_id: profile?.id,
				email: profile?.email,
				method: 'otp_profile_completion',
				is_new_user: true,
				profile_completed: true,
				redirect_url: returnPath,
			});

			return { success: true, emailVerificationRequired: false };
		} catch (error) {
			reportLoginError(error, 'saveNameOnly');
			trackError('login', 'network', thrownMessage(error, 'Network error'), {
				context: 'more_info_name_only',
			});
			return { success: false, error: 'network_error' };
		}
	}, [updateSession, fetchUserProfile, setProfile, returnPath]);

	/**
	 * Submit the more-info step (step 1 of profile update)
	 * Saves name to profile and sends OTP to email
	 * Skips OTP if user already verified this email during login
	 *
	 * Email is optional here. The step used to demand one before it would save
	 * the name, and it is dismissible, so a phone signup who would happily give a
	 * name but not an address left with neither: 362 arrivals, 3 submissions. A
	 * blank email now saves the name and finishes; checkout asks for the address
	 * when a receipt actually needs it.
	 */
	const sendEmailVerification = useCallback(async (currentProfile?: { email?: string; isEmailVerified?: boolean }) => {
		const { name } = fields;
		const email = fields.email.trim();
		const { firstName, lastName } = splitFullName(name);

		// Track profile update attempt
		trackClick('login', 'update_profile', { email });
		trackFunnelEvent('login', 'more_info_submitted', {
			has_name: !!firstName,
			has_email: !!email,
		});

		if (!email) {
			return saveNameOnly(firstName, lastName);
		}

		// The request in flight, so a failure names which of the four it was.
		let request = 'POST /api/user/profile';
		try {
			// Check if email is already verified (user logged in with this email)
			const profileEmail = currentProfile?.email;
			const isEmailAlreadyVerified = currentProfile?.isEmailVerified &&
				profileEmail &&
				profileEmail.toLowerCase() === email.toLowerCase();

			// First save the name to profile
			if (firstName) {
				const profileResponse = await fetch('/api/user/profile', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ firstName, lastName }),
				});
				const profileData = await parseJsonResponse<LoginApiBody>(profileResponse, "POST /api/user/profile");
				if (profileData.success && profileData.profile) {
					setProfile(profileData.profile);
				}
			}

			// If email already verified with same address, skip OTP
			if (isEmailAlreadyVerified) {
				// Refresh session and fetch updated profile (force refresh to get latest)
				request = 'session refresh';
				await updateSession();
				request = 'profile fetch';
				const profile = await fetchUserProfile(true);
				if (profile) setProfile(profile);
				return { success: true, emailVerificationRequired: false };
			}

			// The server refuses a send with no token and asks for the checkbox on a
			// low score (utils/bot-challenge.ts).
			request = 'recaptcha';
			const recaptchaToken = await executeRecaptcha('email_verification');

			// Send OTP to email
			request = 'POST /api/auth/send-code';
			trackApiRequest('login', '/api/auth/send-code', { emailOrPhone: email });
			// Same endpoint, same server-side otp_sent event, so it needs the same
			// session to be filed under -- otherwise this path's OTP events are
			// orphans exactly as the login path's were.
			const response = await postWithBotChallenge('/api/auth/send-code', {
				emailOrPhone: email,
				recaptchaToken,
				funnelSessionId: getFunnelSessionId('login'),
				// /api/user/verify-email redeems an email_capture challenge; without
				// this the code is issued as a login and that redeem finds nothing.
				purpose: 'email_capture',
			});

			const data = await parseJsonResponse<LoginApiBody>(response, "POST /api/auth/send-code");

			if (response.ok && data.success) {
				trackApiResponse('login', '/api/auth/send-code', true, { email });
				return { success: true, emailVerificationRequired: true };
			}

			trackApiResponse('login', '/api/auth/send-code', false, { error: data.error });
			return { success: false, error: data.error || 'Failed to send verification code' };
		} catch (error) {
			reportLoginFetchFailure(error, 'sendEmailVerification', request);
			trackError('login', 'network', thrownMessage(error, 'Network error'), {
				context: 'send_email_verification',
			});
			return { success: false, error: 'network_error' };
		}
	}, [fields, executeRecaptcha, updateSession, setProfile, fetchUserProfile, saveNameOnly]);

	/**
	 * Verify email OTP and complete profile update (step 2)
	 */
	const verifyEmailOTP = useCallback(async (code: string) => {
		const { name } = fields;
		const email = fields.email.trim();
		const { firstName, lastName } = splitFullName(name);

		trackApiRequest('login', '/api/user/verify-email', { email, code: '******' });

		try {
			const response = await fetch('/api/user/verify-email', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					email,
					code,
					firstName,
					lastName,
				}),
			});

			const data = await parseJsonResponse<LoginApiBody>(response, "POST /api/user/verify-email");

			if (response.ok && data.success) {
				trackApiResponse('login', '/api/user/verify-email', true, { email });

				// Refresh NextAuth session to update JWT with new profile data
				await updateSession();

				changeHash("welcome");
				// Force refresh to get the updated profile from server (not cached)
				const profile = await fetchUserProfile(true);
				googleEvent("new_user", profile);
				setProfile(profile);

				// Track complete login after profile completion
				trackComplete('login', {
					user_id: profile?.id,
					email: profile?.email,
					method: 'otp_profile_completion',
					is_new_user: true,
					profile_completed: true,
					redirect_url: returnPath,
				});

				await cleanupLoginSuccess();
				return { success: true, profile };
			} else if (data.errorCode === 'EMAIL_HAS_ACCOUNT' && data.signInCode) {
				// An empty phone account was closed into the account that owns this
				// email; sign in to that one (utils/phone-account-handoff.ts).
				trackApiResponse('login', '/api/user/verify-email', false, { error: 'email_has_account' });
				const result = await authSignIn('passwordless', {
					emailOrPhone: email,
					code: data.signInCode,
					funnelSessionId: getFunnelSessionId('login'),
					redirect: false,
				});
				const profile = result?.ok ? await fetchProfileFromSession() : null;
				if (!profile) {
					createNotification({ type: "action", msg: emailTakenMsg, actionURL: "#login", dismissed: false });
					return { success: false, error: 'email_taken' };
				}
				setProfile(profile);
				trackComplete('login', {
					user_id: profile.id,
					email: profile.email,
					method: 'email_has_account',
					is_new_user: false,
					redirect_url: returnPath,
				});
				changeHash("loggedin");
				await cleanupLoginSuccess();
				return { success: true, profile };
			} else if (response.status === 401) {
				trackApiResponse('login', '/api/user/verify-email', false, { error: 'unauthorized' });
				createNotification({
					type: "action",
					msg: oldTokenMsg,
					actionURL: "#",
					dismissed: false,
				});
				return { success: false, error: 'unauthorized' };
			} else if (data.error?.includes('already associated')) {
				trackApiResponse('login', '/api/user/verify-email', false, { error: 'email_taken' });
				trackError('login', 'profile_update', 'Email already taken', { email });
				createNotification({
					type: "action",
					msg: emailTakenMsg,
					actionURL: "#login",
					dismissed: false,
				});
				return { success: false, error: 'email_taken' };
			}

			trackApiResponse('login', '/api/user/verify-email', false, { error: data.error });
			return { success: false, error: data.error || 'Invalid verification code' };
		} catch (error) {
			reportLoginError(error, 'verifyEmailOTP');
			trackError('login', 'network', thrownMessage(error, 'Network error'), {
				context: 'verify_email',
			});
			return { success: false, error: 'network_error' };
		}
	}, [fields, fetchUserProfile, fetchProfileFromSession, setProfile, cleanupLoginSuccess, returnPath, updateSession]);

	/**
	 * Update user profile (legacy - for non-email updates)
	 */
	const updateUserProfile = useCallback(async () => {
		const { firstName, lastName } = splitFullName(fields.name);

		try {
			const response = await fetch('/api/user/profile', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ firstName, lastName }),
			});

			const data = await parseJsonResponse<LoginApiBody>(response, "POST /api/user/profile");

			if (response.ok && data.success) {
				await updateSession();
				const profile = await fetchUserProfile(true);
				setProfile(profile);
				return { success: true, profile };
			}

			return { success: false, error: data.error };
		} catch (error) {
			reportLoginError(error, 'updateUserProfile');
			return { success: false, error: 'network_error' };
		}
	}, [fields, fetchUserProfile, setProfile, updateSession]);

	/**
	 * The click side of every social button: the funnel start, the click event,
	 * and the pending marker the completion tracker reads after the redirect.
	 * Returns the callback URL the sign-in returns to.
	 */
	const startSocialLogin = useCallback((provider: SocialProvider): string => {
		// Mark funnel as started (social button click counts as interaction)
		onInteraction?.();
		trackClick('login', `social_${provider}`, {
			provider,
			callback_url: returnPath,
		});
		sessionStorage.setItem('pending_social_login', JSON.stringify({
			provider,
			callback_url: returnPath,
			started_at: Date.now(),
		}));
		return returnPath;
	}, [returnPath, onInteraction]);

	/**
	 * Hand a native SDK's token to NextAuth's native-oauth provider and finish:
	 * the verify page when the email needs the code step, a toast for any other
	 * refusal, the signed-in state otherwise. One copy for every native flow.
	 */
	const finishNativeSignIn = useCallback(async (
		provider: SocialProvider,
		token: NativeSignInToken,
		callbackUrl: string,
	): Promise<void> => {
		const result = await authSignIn('native-oauth', {
			provider,
			providerToken: token.providerToken,
			email: token.email || '',
			name: token.name || '',
			userId: token.userId || '',
			callbackUrl,
			redirect: false,
		});
		if (result?.error) {
			// The email needs the code step: go to the verify page, not a toast.
			const verifyPath = oauthVerifyPathFromError(result.error);
			if (verifyPath) {
				push(verifyPath);
				return;
			}
			createNotification({ type: 'error', msg: result.error });
			trackError('login', `social_${provider}`, result.error, { provider });
			return;
		}
		const profile = await fetchUserProfile();
		if (profile) setProfile(profile);
		await cleanupLoginSuccess();
		changeHash('loggedin');
	}, [push, fetchUserProfile, setProfile, cleanupLoginSuccess]);

	/**
	 * Login with Google
	 */
	const loginWithGoogle = useCallback(async () => {
		const callbackUrl = startSocialLogin('google');

		const { isSellwildNative: isGoogleNative } = await import('../../utils/native/sellwild-plugin');
		if (isGoogleNative()) {
			// Google blocks OAuth in embedded WKWebView (disallowed_useragent).
			// Use the native sign-in flow via @codetrix-studio/capacitor-google-auth
			// and forward the ID token to NextAuth's native-oauth provider.
			try {
				const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
				await GoogleAuth.initialize();
				const user = await GoogleAuth.signIn();
				const idToken = user.authentication?.idToken;
				if (!idToken) throw new Error('Google did not return an ID token');
				await finishNativeSignIn('google', { providerToken: idToken, email: user.email, name: user.name, userId: user.id }, callbackUrl);
				return;
			} catch (err) {
				throw thrownAsError(err, 'Google Sign-In failed');
			}
		}
		await authSignIn('google', { callbackUrl });
	}, [startSocialLogin, finishNativeSignIn]);

	/**
	 * Login with Facebook
	 */
	const loginWithFacebook = useCallback(async () => {
		const callbackUrl = startSocialLogin('facebook');

		const { Capacitor } = await import('@capacitor/core');
		const fbNativePlatform = Capacitor.getPlatform();
		if (fbNativePlatform === 'ios' || fbNativePlatform === 'android') {
			// Facebook's webview policy is trending stricter; run the native
			// SDK path on mobile and hand the access token to native-oauth.
			let noTokenError: Error | null = null;
			try {
				const { FacebookLogin } = await import('@capacitor-community/facebook-login');
				// The plugin returns FB's Limited Login id_token (a JWT) in
				// `accessToken.token` regardless of tracking mode — see
				// FacebookLoginPlugin.swift:getCurrentAccessToken. The server
				// verifies it against Facebook's OIDC JWKS in verify-token.ts.
				const fbResult = await FacebookLogin.login({ permissions: ['public_profile', 'email'] });
				// iOS may resolve a cancel with no payload at all, so guard the result too.
				const accessToken = fbResult?.accessToken?.token;
				if (!accessToken) {
					// A tap on Cancel resolves with no token, not a rejection: iOS does a
					// bare call.resolve(), Android resolves { accessToken: null }. Record it
					// as its own non-error step so it does not count as a sign-in failure.
					trackFunnelEvent('login', 'social_cancelled', {
						provider: 'facebook',
						platform: fbNativePlatform,
						reason: 'no_access_token',
					}, { immediate: true });
					noTokenError = new Error('Facebook did not return an access token');
					throw noTokenError;
				}
				// Ask Facebook for the profile so we can populate email/name.
				const fbProfile = await FacebookLogin.getProfile<{
					id: string; name?: string; email?: string;
				}>({ fields: ['id', 'name', 'email'] });
				await finishNativeSignIn('facebook', {
					providerToken: accessToken,
					email: fbProfile.email,
					name: fbProfile.name,
					userId: fbProfile.id,
				}, callbackUrl);
				return;
			} catch (err) {
				const error = thrownAsError(err, 'Facebook Sign-In failed');
				// A cancel was already recorded as social_cancelled above.
				if (error === noTokenError) throw error;
				// The native SDK rejected (plugin or FB error). Record it on the login
				// funnel so it is seen without logging the user's FB profile.
				trackError('login', 'social_facebook', error.message, {
					provider: 'facebook',
					platform: fbNativePlatform,
				});
				throw error;
			}
		}
		await authSignIn('facebook', { callbackUrl });
	}, [startSocialLogin, finishNativeSignIn]);

	/**
	 * Login with Apple
	 */
	const loginWithApple = useCallback(async () => {
		const callbackUrl = startSocialLogin('apple');

		const { Capacitor } = await import('@capacitor/core');
		const applePlatform = Capacitor.getPlatform();
		if (applePlatform === 'ios') {
			// Google/Apple/Facebook OAuth in an embedded WKWebView is blocked
			// (disallowed_useragent). On native iOS, drive Apple Sign-In
			// through AuthenticationServices via the Capacitor plugin and
			// hand the resulting identity token to NextAuth's native-oauth
			// credentials provider.
			try {
				const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');
				const appleResult = await SignInWithApple.authorize({
					clientId: 'com.sellwild.app',
					redirectURI: 'https://sellwild.com',
					scopes: 'email name',
					state: Math.random().toString(36).slice(2),
					nonce: Math.random().toString(36).slice(2),
				});
				const { identityToken, email, givenName, familyName, user: appleUserId } = appleResult.response;
				await finishNativeSignIn('apple', {
					providerToken: identityToken,
					email,
					// Apple sends the name on the first authorization only.
					name: joinNameParts(givenName, familyName),
					userId: appleUserId,
				}, callbackUrl);
				return;
			} catch (err) {
				// User-cancelled sign-in raises — don't fall through to the
				// web flow, since that would hit disallowed_useragent. Just
				// surface a gentle error and bail.
				throw thrownAsError(err, 'Apple Sign-In failed');
			}
		}

		if (applePlatform === 'android') {
			// No native Apple Sign-In SDK exists for Android. Apple's web
			// OAuth works on Android, but it must NOT run inside the
			// Capacitor WebView (disallowed_useragent). Open the authorize
			// URL in Chrome Custom Tabs, listen for the sellwildapp://
			// deep link our `apple-native-callback` endpoint redirects to,
			// then forward the id_token to the native-oauth provider.
			try {
				const [{ Browser }, { App }] = await Promise.all([
					import('@capacitor/browser'),
					import('@capacitor/app'),
				]);

				// NEXTAUTH_URL is server-only (no NEXT_PUBLIC_ prefix) so it's
				// undefined in client code. Resolve the redirect against the
				// API origin the Capacitor build is wired to (stg vs prod),
				// matching whatever backend will receive the form_post and
				// 303 us back to sellwildapp://.
				const { getApiOrigin } = await import('../../utils/native/api-base');
				const state = Math.random().toString(36).slice(2);
				const authorizeUrl = appleAuthorizeUrl({
					// Apple's web flow uses the Service ID (e.g. com.sellwild.www) as
					// client_id, NOT the bundle ID. Inlined at build time via NEXT_PUBLIC.
					serviceId: process.env.NEXT_PUBLIC_APPLE_SERVICE_ID,
					apiOrigin: getApiOrigin(),
					state,
					nonce: Math.random().toString(36).slice(2),
				});

				// Race the deep-link callback against a timeout so we don't
				// leave the listener attached forever if the user cancels by
				// dismissing the Custom Tab without completing sign-in.
				const tokenPromise = new Promise<AppleDeepLinkToken>((resolve, reject) => {
					let settled = false;
					const handle = App.addListener('appUrlOpen', async (event) => {
						if (settled) return;
						const url = event.url;
						if (!isAppleDeepLink(url)) return;
						settled = true;
						void (await handle).remove();
						void Browser.close();
						try {
							resolve(readAppleDeepLink(url, state));
						} catch (err) {
							reject(err);
						}
					});
				});

				await Browser.open({ url: authorizeUrl, windowName: '_self' });
				const { idToken, email, name } = await tokenPromise;
				await finishNativeSignIn('apple', { providerToken: idToken, email, name }, callbackUrl);
				return;
			} catch (err) {
				throw thrownAsError(err, 'Apple Sign-In failed');
			}
		}

		await authSignIn('apple', { callbackUrl });
	}, [startSocialLogin, finishNativeSignIn]);

	return {
		sendLoginCode,
		checkCodeDelivery,
		verifyLoginOTP,
		updateUserProfile,
		sendEmailVerification,
		verifyEmailOTP,
		loginWithGoogle,
		loginWithFacebook,
		loginWithApple,
		cleanupLoginSuccess,
	};
};
